import { useState, useEffect, useRef } from "react";
import { AiOutlineSend } from "react-icons/ai";
import { useAuth0 } from "@auth0/auth0-react";
import { createUser } from "../Utils/utils";
import {
  saveUrl,
  fetchBlogDetails,
  acceptOnboarding,
  createDrafts,
  taskComplete,
} from "../Utils/chatUtils";
import useUserStore from "../store";
import { useNavigate } from "react-router-dom";
import Logo from "../Images/logo.png";
import "react-tooltip/dist/react-tooltip.css";
import axios from "axios";
import { HashLoader } from "react-spinners";
import { Tooltip } from "react-tooltip";
import { FaCheck, FaPlus, FaTimes, FaQuestionCircle, FaBars } from 'react-icons/fa';

export function ChatInterface() {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();
  const [url, setUrl] = useState(""); // Changed initial state to empty string
  const { userId, setUserId, setBlogs, setDrafts, setLoading, setError, drafts } =
    useUserStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusText, setStatusText] = useState("");
  const navigate = useNavigate();
  const homePageSubmittedURL = "homePageSubmittedURL";
  const isFirstRender = useRef(true);
  const cachedUrlProcessed = useRef(false);
  const [draftsFetched, setDraftsFetched] = useState(false);
  const userIdFetched = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [blogDetails, setBlogDetails] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [summary, setSummary] = useState("");
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [loadingMessages, setLoadingMessages] = useState([
    "Analyzing your website...",
    "Researching industry trends...",
    "Gathering relevant data...",
    "Generating initial draft...",
    "Polishing the content...",
    "Optimizing for search engines...",
    "Checking for plagiarism...",
    "Adding engaging visuals...",
    "Final review and edits...",
    "Preparing for publishing...",
    "Creating Drafts... (Checking Status)",
  ]);
  const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] = useState(0);

  const baseURL = "https://trendhopper-c3934b7293e6.herokuapp.com";  // Replace with your actual base URL
    const contentRef = useRef(null); // Ref for the content area


  const processCachedUrl = (id) => {
    const cachedUrl = localStorage.getItem(homePageSubmittedURL);
    if (cachedUrl && isFirstRender.current && !cachedUrlProcessed.current) {
      setUrl(cachedUrl);
      localStorage.removeItem(homePageSubmittedURL);
      handleUrlSubmission(cachedUrl, id);
      isFirstRender.current = false;
      cachedUrlProcessed.current = true;
    }
    isFirstRender.current = false;
  };

  useEffect(() => {
    if (isAuthenticated && user && !userIdFetched.current) {

      const fetchUserId = async () => {
        try {
          const id = await createUser(user.email);
          if (id) {
            setUserId(id);
            processCachedUrl(id);
            userIdFetched.current = true;
          }
        } catch (error) {
          console.error("Error fetching or creating user:", error);
        }
      };

      fetchUserId();
    }
  }, [isAuthenticated, isLoading, user, setUserId]);

  const validateUrl = (url) => {
    if (!url) {
        return false;
    }
    const trimmedUrl = url.trim();
    if (trimmedUrl === "") {
        return false;
    }

    if (!trimmedUrl.startsWith("http://") && !trimmedUrl.startsWith("https://")) {
      return false;
    }

    try {
      const urlObj = new URL(trimmedUrl);

      const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(urlObj.hostname)) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  };

  const handleInputChange = (e) => {
    setUrl(e.target.value);
  };

  const handleUrlSubmission = async (submittedUrl, currentUserId) => {
    const urlToSubmit = submittedUrl || url;

    // Validate only if URL is not empty
    if (urlToSubmit !== "" && !validateUrl(urlToSubmit)) { // Corrected validation condition
      alert("Please enter a valid URL including 'http://' or 'https://'.");
      return;
    }
    const finalUrl = urlToSubmit; // No longer modifying the URL based on "https://"


    setIsProcessing(true);
    setStatusText("Analyzing your website...");
    try {
      const success = await saveUrl(finalUrl, currentUserId || userId);  // Use finalUrl

      if (success) {
        const details = await fetchBlogDetails(finalUrl, currentUserId || userId); // and here
        setBlogDetails(details);
        setKeywords(details.keywords || []);
        setSummary(details.summary || "");
        setOnboardingStep(2);
      } else {
        alert("An error occurred while saving the blog.");
      }
    } catch (error) {
      console.error("Error in handleUrlSubmit:", error);
      alert("An error occurred while processing your request.");
    } finally {
      setIsProcessing(false);
      setStatusText("");
    }
  };

    const handleSkipUrl = () => {
      handleUrlSubmission("", userId); // Pass empty URL to backend on skip
    };

  const handleAddKeyword = () => {
    if (newKeyword.trim() !== "") {
      setKeywords([...keywords, { keyword: newKeyword.trim() }]);
      setNewKeyword("");
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setKeywords(keywords.filter((_, index) => index !== indexToRemove));
  };

  const handleSummaryChange = (e) => {
    setSummary(e.target.value);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isProcessing) {
        setCurrentLoadingMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);      }
    }, 11000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isProcessing, loadingMessages.length, currentLoadingMessageIndex]);

  const handleAcceptOnboarding = async () => {
    setIsProcessing(true);
    setStatusText("Finalizing setup...");

    try {
      if (!blogDetails?.blogId) {
        throw new Error("Blog ID is missing.");
      }

      const keywordValues = keywords.map((keyword) => keyword.keyword);

      await acceptOnboarding(blogDetails.blogId, summary, keywordValues);

      setStatusText("Creating Drafts...");
      let task = await createDrafts(url, userId); //keep url (not finalUrl) for reddit search.
      let retryCount = 0;
      const maxRetries = 1;
      let isComplete = false;
      let drafts = null;

      if (task && task.task_id) {
        const createDraftsTask = {
          taskId: task.task_id,
          status: "pending",
          message: "Creating Drafts...",
          timestamp: Date.now(),
        };
        localStorage.setItem("createDraftsTask", JSON.stringify(createDraftsTask));

        while (!isComplete && retryCount <= maxRetries) {
          try {
            setStatusText(loadingMessages[currentLoadingMessageIndex]);
            await new Promise((resolve) => setTimeout(resolve, 6000));
            const taskStatus = await taskComplete(task.task_id);

            if (taskStatus.status === "PENDING") {
              isComplete = false;
            } else if (taskStatus.status === "SUCCESS") {
              isComplete = true;
              try {
                const response = await axios.get(`${baseURL}/user/${userId}/draftsbyurl/`);
                drafts = response.data.drafts;
                const blogId = response.data.blogId;


                setDrafts((prevDrafts) => ({
                  ...prevDrafts,
                  [blogId]: drafts,
                }));
              } catch (error) {
                console.error("Error fetching drafts after task completion:", error);
                setError("Failed to fetch drafts after task completion. Please try again.");
              }
            } else {
              isComplete = true;
              console.warn("Task completed but no drafts returned.");
            }
          } catch (error) {
            console.error(`Error checking task status for task ${task.task_id}:`, error);
            retryCount++;
            setStatusText(`Error creating drafts, retrying... (${retryCount}/${maxRetries})`);

            if (retryCount <= maxRetries) {
              task = await createDrafts(url, userId);
              if (!task || !task.task_id) {
                console.error("Failed to get task ID after retry");
                break;
              }

              const updatedCreateDraftsTask = {
                taskId: task.task_id,
                status: "pending",
                message: "Creating Drafts...",
                timestamp: Date.now(),
              };
              localStorage.setItem("createDraftsTask", JSON.stringify(updatedCreateDraftsTask));
            }
          }
        }

        if (isComplete) {
          setStatusText("Drafts Created!");
          localStorage.removeItem("createDraftsTask");

          if (drafts && Array.isArray(drafts)) {
            const earliestDraft = drafts.reduce((prev, current) =>
              new Date(current.date) < new Date(prev.date) ? current : prev
            );
            await acceptDraftAndStoreTask(earliestDraft.id);
            navigate("/articles-table-view");

          } else {
            console.warn("No drafts found after successful draft creation.");
            alert("No drafts were created. Please try again.");
          }
        } else {
          console.error("Failed to create drafts after retry");
          alert("Failed to create drafts after multiple attempts. Please try again.");
        }
      } else {
        console.error("Failed to get task ID from createDrafts");
        alert("Failed to create drafts. Please try again.");
      }

      setOnboardingStep(2);
    } catch (error) {
      console.error("Error in handleAcceptOnboarding:", error);
      alert("An error occurred while accepting onboarding.");
    } finally {
      setIsProcessing(false);
      setStatusText("");
        if (contentRef.current) { // Scroll to bottom after processing
          contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
    }
  };

  const acceptDraftAndStoreTask = async (draftId) => {
    const messages = [
      "Researching Reddit...",
      "Creating Outline...",
      "Drafting Content...",
      "Finalizing Article...",
    ];

    const optimisticTask = {
      status: "pending",
      message: messages[0],
      taskId: null,
      action: "accept",
      messageIndex: 0,
      timestamp: Date.now(),
    };

    let existingTasks = JSON.parse(localStorage.getItem("processingTasks") || "{}");

    if (typeof existingTasks !== "object" || existingTasks === null) {
      existingTasks = {};
    }

    try {
      const response = await axios.get(` ${baseURL}/accept-draft/${draftId}/`);
      const { task_id } = response.data;

      const updatedTask = { ...optimisticTask, taskId: task_id };
      existingTasks[draftId] = updatedTask;
      localStorage.setItem("processingTasks", JSON.stringify(existingTasks));

    } catch (err) {
      console.error(`Error accepting draft ${draftId}:`, err);
      setError(`Failed to accept draft ${draftId}. Please try again.`);

      existingTasks = JSON.parse(localStorage.getItem("processingTasks") || "{}");
      delete existingTasks[draftId];
      localStorage.setItem("processingTasks", JSON.stringify(existingTasks));
    }
  };

  useEffect(() => {
    if (userId && !draftsFetched) {
      const fetchDrafts = async () => {
        try {
          const response = await axios.get(`${baseURL}/user/${userId}/draftsbyurl/`);
          const blogId = response.data.blogId;

          setDrafts((prevDrafts) => ({
            ...prevDrafts,
            [blogId]: response.data.drafts,
          }));
          setDraftsFetched(true);
        } catch (error) {
          console.error("Error fetching drafts:", error);
          setError("Failed to fetch drafts. Please try again.");
        }
      };

      fetchDrafts();
    }
  }, [userId, setDrafts, setError, draftsFetched]);

    const renderOnboardingStep = () => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleAddKeyword();
      }
    };
    switch (onboardingStep) {
      case 1:
        return (
          // Step 1: URL Input
          <div className="p-6 rounded-lg shadow-md flex flex-col transition-all duration-300 ease-in-out">
            <p className="text-gray-800 text-lg mb-4">
              To get started, please provide the URL of your website.
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); handleUrlSubmission(); }}
              className="flex flex-col gap-3"
            >
              <div className="relative w-full">
                <label htmlFor="website-url" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  Website URL
                  <Tooltip id="url-help" className="max-w-xs" />
                  <button type="button" className="text-gray-400 hover:text-gray-500" data-tooltip-id="url-help" data-tooltip-content="Enter the full URL">
                    <FaQuestionCircle />
                  </button>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className={`text-gray-500 ${validateUrl(url) ? "text-green-500" : ""}`}>#</span>
                  </div>
                  <input
                    type="text"
                    id="website-url"
                    placeholder="https://www.yourwebsite.com"
                    value={url}
                    onChange={handleInputChange}
                    disabled={isProcessing}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pl-7 ${isProcessing ? "cursor-not-allowed bg-gray-50" : "bg-white"} ${validateUrl(url) ? "border-green-500" : "border-gray-300"} text-gray-800`}
                    aria-describedby="website-url-description"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500" id="website-url-description">
                  Enter the full URL of your website.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                    type="submit"
                    disabled={isProcessing || !userId}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <AiOutlineSend className="mr-2 -ml-1 h-5 w-5" />
                    Submit
                  </button>
                <button
                    type="button"
                    onClick={handleSkipUrl}
                    disabled={isProcessing}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Skip
                </button>
               </div>
              {isProcessing && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <HashLoader color="#4F46E5" size={20} />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{statusText}</span>
                      <span className="text-xs text-gray-500">This may take a few moments...</span>
                    </div>
                  </div>
                  <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 rounded-full transition-all duration-500 ease-out" style={{ width: `${((currentLoadingMessageIndex + 1) / loadingMessages.length) * 100}%` }} />
                  </div>
                </div>
              )}
            </form>
          </div>
        );
      case 2:
        return (
          <div className="p-6 rounded-lg shadow-md flex flex-col transition-all duration-300 ease-in-out">
            <p className="text-gray-800 text-lg mb-4">
              Help us understand your blog better.
            </p>

            <div className="mb-4">
              <label htmlFor="summary" className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                <span className="flex items-center gap-2">Blog Summary
                  <Tooltip id="summary-help" className="max-w-xs" />
                  <button type="button" className="text-gray-400 hover:text-gray-500" data-tooltip-id="summary-help" data-tooltip-content="Describe your blog">
                    <FaQuestionCircle />
                  </button>
                </span>
                <span className="text-xs text-gray-500">{summary.length}</span>
              </label>
              <textarea
                id="summary"
                rows="3"
                maxLength={500}
                placeholder="Describe your blog's main topics..."
                value={summary}
                onChange={handleSummaryChange}
                disabled={isProcessing}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-800"
              />
            </div>

            <div className="mb-2 flex-grow" style={{ overflow: 'hidden' }}>
              <label className="block text-sm font-medium text-gray-700">Relevant Keywords:</label>
              <div className="max-h-40 overflow-y-auto pr-2">
                <div className="flex flex-wrap gap-2 mt-1">
                {keywords.map((keyword, index) => (
  <span key={index} className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-0.5 text-sm font-medium text-indigo-800">
    {keyword.keyword}
    <button
      type="button"
      className="ml-1.5 flex-shrink-0 rounded-full p-0.5 text-indigo-500 hover:bg-indigo-200 focus:outline-none"
      onClick={() => handleRemoveKeyword(index)}
      disabled={isProcessing}
    >
      <FaTimes className="h-3 w-3" />
    </button>
  </span>
))}
                </div>
              </div>
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  placeholder="Add a new keyword..."
                  onKeyDown={handleKeyDown}
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  disabled={isProcessing}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-800 transition-all duration-200"
                />
                <button
                    onClick={handleAddKeyword}
                    disabled={isProcessing}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPlus className="mr-1"/>
                  Add
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500">Enter keywords.</p>
            </div>

            {/* Finalize Button */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={handleAcceptOnboarding}
                disabled={isProcessing}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-600 hover:via-red-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
>
                <FaCheck className="mr-2" />
                Finalize Setup
              </button>
            </div>

            {/* Loading Indicators */}
            {isRefreshing && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <HashLoader color="#4F46E5" size={20} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">Done!</span>
                    <span className="text-xs text-gray-500">Let's get started.</span>
                  </div>
                </div>
              </div>
            )}
            {isProcessing && (
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <HashLoader color="#4F46E5" size={20} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{statusText}</span>
                    <span className="text-xs text-gray-500">This may take a few moments...</span>
                  </div>
                </div>
                <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-600 rounded-full transition-all duration-500 ease-out" style={{ width: `${((currentLoadingMessageIndex + 1) / loadingMessages.length) * 100}%` }} />
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full mx-auto max-w-2xl bg-white rounded-lg shadow-xl flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-t-lg text-white px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
            <img src={Logo || "/placeholder.svg"} alt="Trendhopper Logo" className="h-8 w-8" />
            <span className="text-xl font-semibold">Trendhopper</span>
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isProcessing}
                className="text-gray-200 hover:text-white focus:outline-none flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-sm font-medium">Menu</span>
              <FaBars />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 max-w-[calc(100vw-2rem)]">
                <div className="px-4 py-2 text-gray-800 text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap" title={user?.email || "User Email"}>
                  {user?.email || "User Email"}
                </div>
                <button
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                >
                  Logout
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white p-4 sm:p-10 flex rounded-b-lg flex-col flex-1" ref={contentRef}> {/* Added ref here */}
        <div className="text-gray-800 text-2xl font-semibold mb-6">Account Setup</div>

        {/* Stepper */}
        <ol className="flex flex-col sm:flex-row items-start sm:items-center w-full space-y-4 sm:space-y-0 sm:space-x-8 rtl:space-x-reverse mb-5">
          {/* Step 1 */}
          <li className={`flex items-center transition-all duration-300 transform ${onboardingStep === 1 ? "text-blue-600 dark:text-blue-500 scale-105" : "text-gray-500 dark:text-gray-400"} space-x-2.5 rtl:space-x-reverse`}>
            <span className={`flex items-center justify-center w-8 h-8 border ${onboardingStep === 1 ? "border-blue-600 dark:border-blue-500" : "border-gray-500 dark:border-gray-400"} rounded-full shrink-0`}>1</span>
            <span>
              <h3 className="font-medium leading-tight">Business Profile</h3>
              <p className="text-sm">Provide URL</p>
            </span>
          </li>
          {/* Step 2 */}
          <li className={`flex items-center transition-all duration-300 transform ${onboardingStep === 2 ? "text-blue-600 dark:text-blue-500 scale-105" : "text-gray-500 dark:text-gray-400"} space-x-2.5 rtl:space-x-reverse`}>
            <span className={`flex items-center justify-center w-8 h-8 border ${onboardingStep === 2 ? "border-blue-600 dark:border-blue-500" : "border-gray-500 dark:border-gray-400"} rounded-full shrink-0`}>2</span>
            <span>
              <h3 className="font-medium leading-tight">Blog Setup</h3>
              <p className="text-sm">Review suggested keywords</p>
            </span>
          </li>
        </ol>

        {/* Progress Bar */}
        <div className="w-full h-1 bg-gray-100 rounded-full mb-8">
          <div className="h-full bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-full transition-all duration-500 ease-in-out" style={{ width: `${onboardingStep === 1 ? "50%" : "100%"}` }} />
        </div>

        {/* Onboarding Steps Content (Scrollable) + Disclaimer */}
        <div className="flex flex-col flex-grow">
            <div className="overflow-y-auto flex-grow">
              {renderOnboardingStep()}
            </div>
            <div className="mt-8 text-gray-500 text-center pt-4">
              <p className="text-sm">Disclaimer: Trendhopper is not affiliated with Reddit.</p>
            </div>
          </div>
      </div>
    </div>
  );
}
